import {
  adminV2ExactIdentitySchema,
  adminV2StableIdentitySchema,
} from '@/admin-v2/foundation/admin-v2-identity';
import { appIconKeys } from '@/components/icons/app-icon-catalog';
import { z } from 'zod';

/**
 * Custom Workspace Tool Catalog marks are intentionally stored as references
 * to server-owned SVG assets.  A public URL or inline SVG is not part of the
 * catalog contract.
 */
export const ADMIN_V2_WORKSPACE_TOOL_CATALOG_CUSTOM_ICON_MIME_TYPE =
  'image/svg+xml' as const;

export const ADMIN_V2_WORKSPACE_TOOL_CATALOG_CUSTOM_ICON_MAX_BYTES = 256 * 1024;

export const ADMIN_V2_WORKSPACE_TOOL_CATALOG_CUSTOM_ICON_STORAGE_PREFIX =
  'cms/v2/workspace-tool-catalog-icons/';

export const adminV2WorkspaceToolCatalogCustomIconAssetIdSchema =
  adminV2StableIdentitySchema;

const customIconStorageKeyPattern = new RegExp(
  `^${escapeRegExp(
    ADMIN_V2_WORKSPACE_TOOL_CATALOG_CUSTOM_ICON_STORAGE_PREFIX
  )}[a-zA-Z0-9][a-zA-Z0-9_-]{0,80}\\.svg$`
);

export const adminV2WorkspaceToolCatalogCustomIconStorageKeySchema = z
  .string()
  .regex(
    customIconStorageKeyPattern,
    'Custom icon storage key must be a server-owned SVG asset key.'
  );

export const adminV2WorkspaceToolCatalogCustomIconReferenceSchema = z
  .object({
    iconSource: z.literal('custom'),
    assetId: adminV2WorkspaceToolCatalogCustomIconAssetIdSchema,
    storageKey: adminV2WorkspaceToolCatalogCustomIconStorageKeySchema,
    mimeType: z.literal(ADMIN_V2_WORKSPACE_TOOL_CATALOG_CUSTOM_ICON_MIME_TYPE),
  })
  .strict()
  .superRefine((reference, context) => {
    const expectedStorageKey = `${ADMIN_V2_WORKSPACE_TOOL_CATALOG_CUSTOM_ICON_STORAGE_PREFIX}${reference.assetId}.svg`;
    if (reference.storageKey !== expectedStorageKey) {
      context.addIssue({
        code: 'custom',
        message: 'Custom icon storage key must match its asset id.',
        path: ['storageKey'],
      });
    }
  });

export const adminV2WorkspaceToolCatalogAppIconReferenceSchema = z
  .object({
    iconSource: z.literal('app'),
    iconKey: adminV2ExactIdentitySchema.refine(
      (iconKey) =>
        appIconKeys.includes(iconKey as (typeof appIconKeys)[number]),
      'App icon key is not whitelisted.'
    ),
  })
  .strict();

export const adminV2WorkspaceToolCatalogIconSchema = z.discriminatedUnion(
  'iconSource',
  [
    adminV2WorkspaceToolCatalogAppIconReferenceSchema,
    adminV2WorkspaceToolCatalogCustomIconReferenceSchema,
  ]
);

export type AdminV2WorkspaceToolCatalogAppIconReference = z.infer<
  typeof adminV2WorkspaceToolCatalogAppIconReferenceSchema
>;

export type AdminV2WorkspaceToolCatalogCustomIconReference = z.infer<
  typeof adminV2WorkspaceToolCatalogCustomIconReferenceSchema
>;

export type AdminV2WorkspaceToolCatalogIcon = z.infer<
  typeof adminV2WorkspaceToolCatalogIconSchema
>;

export type AdminV2WorkspaceToolCatalogCustomIconUploadResponse = {
  icon: AdminV2WorkspaceToolCatalogCustomIconReference;
  url: string;
};

export type AdminV2WorkspaceToolCatalogSvgValidationCode =
  | 'invalid_mime_type'
  | 'empty_svg'
  | 'svg_too_large'
  | 'invalid_utf8'
  | 'invalid_svg';

export type AdminV2WorkspaceToolCatalogSvgValidationFailure = {
  ok: false;
  code: AdminV2WorkspaceToolCatalogSvgValidationCode;
  message: string;
};

export type AdminV2WorkspaceToolCatalogSvgValidationSuccess = {
  ok: true;
  svg: string;
  byteSize: number;
};

export type AdminV2WorkspaceToolCatalogSvgValidationResult =
  | AdminV2WorkspaceToolCatalogSvgValidationFailure
  | AdminV2WorkspaceToolCatalogSvgValidationSuccess;

type SvgBytes = Uint8Array | ArrayBuffer;

const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
const XLINK_NAMESPACE = 'http://www.w3.org/1999/xlink';

const unsafeSvgElements = new Set([
  'animate',
  'animatemotion',
  'animatetransform',
  'audio',
  'base',
  'canvas',
  'embed',
  'foreignobject',
  'frame',
  'frameset',
  'iframe',
  'image',
  'link',
  'mpath',
  'object',
  'script',
  'set',
  'video',
]);

const knownXmlEntities = new Set(['amp', 'apos', 'gt', 'lt', 'quot']);

type ParsedSvgTag = {
  name: string;
  attributes: Map<string, { originalName: string; value: string }>;
  selfClosing: boolean;
  closing: boolean;
};

type SvgValidationError = {
  code: 'invalid_svg';
  message: string;
};

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function invalidSvg(message: string): SvgValidationError {
  return { code: 'invalid_svg', message };
}

function toBytes(value: SvgBytes) {
  if (value instanceof Uint8Array) {
    return new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
  }
  return new Uint8Array(value);
}

function isValidXmlCodePoint(codePoint: number) {
  return (
    Number.isInteger(codePoint) &&
    (codePoint === 0x9 ||
      codePoint === 0xa ||
      codePoint === 0xd ||
      (codePoint >= 0x20 && codePoint <= 0xd7ff) ||
      (codePoint >= 0xe000 && codePoint <= 0xfffd) ||
      (codePoint >= 0x10000 && codePoint <= 0x10ffff))
  );
}

function decodeXmlEntities(value: string) {
  return value.replace(
    /&(#x[0-9a-f]+|#\d+|amp|apos|gt|lt|quot);/gi,
    (entity, body: string) => {
      if (body.toLowerCase() === 'amp') return '&';
      if (body.toLowerCase() === 'apos') return "'";
      if (body.toLowerCase() === 'gt') return '>';
      if (body.toLowerCase() === 'lt') return '<';
      if (body.toLowerCase() === 'quot') return '"';

      const codePoint = body.toLowerCase().startsWith('#x')
        ? Number.parseInt(body.slice(2), 16)
        : Number.parseInt(body.slice(1), 10);
      if (!isValidXmlCodePoint(codePoint)) {
        return entity;
      }
      return String.fromCodePoint(codePoint);
    }
  );
}

function assertXmlEntityReferences(value: string): SvgValidationError | null {
  let cursor = 0;
  while (cursor < value.length) {
    const ampersand = value.indexOf('&', cursor);
    if (ampersand === -1) return null;
    const semicolon = value.indexOf(';', ampersand + 1);
    if (semicolon === -1) {
      return invalidSvg('SVG contains an unterminated XML entity.');
    }
    const body = value.slice(ampersand + 1, semicolon);
    if (knownXmlEntities.has(body.toLowerCase())) {
      cursor = semicolon + 1;
      continue;
    }
    if (!/^#(?:x[0-9a-f]+|\d+)$/i.test(body)) {
      return invalidSvg('SVG contains an unsupported XML entity.');
    }
    const codePoint = body.toLowerCase().startsWith('#x')
      ? Number.parseInt(body.slice(2), 16)
      : Number.parseInt(body.slice(1), 10);
    if (!isValidXmlCodePoint(codePoint)) {
      return invalidSvg('SVG contains an invalid XML character reference.');
    }
    cursor = semicolon + 1;
  }
  return null;
}

function findTagEnd(markup: string, start: number) {
  let quote: '"' | "'" | null = null;
  for (let index = start + 1; index < markup.length; index += 1) {
    const character = markup[index];
    if (quote) {
      if (character === quote) quote = null;
      continue;
    }
    if (character === '"' || character === "'") {
      quote = character;
    } else if (character === '>') {
      return index;
    }
  }
  return -1;
}

function parseSvgTag(source: string): ParsedSvgTag | SvgValidationError {
  let body = source.slice(1, -1);
  const closing = body.startsWith('/');
  if (closing) body = body.slice(1);
  body = body.trim();

  const selfClosing = !closing && /\/\s*$/.test(body);
  if (selfClosing) body = body.replace(/\/\s*$/, '').trim();
  if (!body) return invalidSvg('SVG contains an empty tag.');

  const nameMatch = /^[A-Za-z_][A-Za-z0-9_.:-]*/.exec(body);
  if (!nameMatch) return invalidSvg('SVG contains a malformed tag name.');
  const name = nameMatch[0];
  let cursor = name.length;
  const attributes = new Map<string, { originalName: string; value: string }>();

  if (closing) {
    if (body.slice(cursor).trim()) {
      return invalidSvg('SVG closing tags cannot contain attributes.');
    }
    return { name, attributes, selfClosing: false, closing: true };
  }

  while (cursor < body.length) {
    while (/\s/.test(body[cursor] ?? '')) cursor += 1;
    if (cursor >= body.length) break;

    const attributeMatch = /^[A-Za-z_][A-Za-z0-9_.:-]*/.exec(
      body.slice(cursor)
    );
    if (!attributeMatch) {
      return invalidSvg('SVG contains a malformed attribute name.');
    }
    const originalName = attributeMatch[0];
    const key = originalName.toLowerCase();
    cursor += originalName.length;
    while (/\s/.test(body[cursor] ?? '')) cursor += 1;
    if (body[cursor] !== '=') {
      return invalidSvg('SVG attributes must have an explicit value.');
    }
    cursor += 1;
    while (/\s/.test(body[cursor] ?? '')) cursor += 1;
    const quote = body[cursor];
    if (quote !== '"' && quote !== "'") {
      return invalidSvg('SVG attribute values must be quoted.');
    }
    cursor += 1;
    const valueStart = cursor;
    while (cursor < body.length && body[cursor] !== quote) cursor += 1;
    if (cursor >= body.length) {
      return invalidSvg('SVG contains an unterminated attribute value.');
    }
    const value = body.slice(valueStart, cursor);
    if (value.includes('<')) {
      return invalidSvg(
        'SVG attribute values cannot contain a less-than sign.'
      );
    }
    if (attributes.has(key)) {
      return invalidSvg(`SVG attribute ${originalName} is duplicated.`);
    }
    attributes.set(key, { originalName, value });
    cursor += 1;
  }

  return { name, attributes, selfClosing, closing: false };
}

function assertSafeCss(value: string): SvgValidationError | null {
  if (/@import\b|expression\s*\(|javascript\s*:|vbscript\s*:/i.test(value)) {
    return invalidSvg('SVG CSS contains an executable or imported resource.');
  }

  const urlPattern = /url\s*\(\s*(["']?)(.*?)\1\s*\)/gi;
  for (const match of value.matchAll(urlPattern)) {
    const target = decodeXmlEntities(match[2].trim());
    if (!target.startsWith('#') || target.length < 2) {
      return invalidSvg('SVG CSS may reference only local fragments.');
    }
  }
  return null;
}

function assertSafeAttribute(
  name: string,
  rawValue: string
): SvgValidationError | null {
  const value = decodeXmlEntities(rawValue).trim();
  const entityError = assertXmlEntityReferences(rawValue);
  if (entityError) return entityError;

  if (name.startsWith('on')) {
    return invalidSvg('SVG event-handler attributes are not allowed.');
  }
  if (name === 'href' || name === 'xlink:href') {
    if (!value.startsWith('#') || value.length < 2) {
      return invalidSvg(
        'SVG href attributes may reference only local fragments.'
      );
    }
  }
  if (name === 'src' || name === 'action' || name === 'formaction') {
    return invalidSvg(`SVG ${name} attributes are not allowed.`);
  }
  if (name === 'xml:base') {
    return invalidSvg('SVG xml:base attributes are not allowed.');
  }
  if (/javascript\s*:|vbscript\s*:|data\s*:/i.test(value)) {
    return invalidSvg(
      'SVG attributes may not contain executable or external URLs.'
    );
  }

  const cssError = assertSafeCss(value);
  return cssError;
}

function inspectSvgMarkup(markup: string): SvgValidationError | null {
  for (const character of markup) {
    const codePoint = character.codePointAt(0) ?? 0;
    if (
      codePoint === 0 ||
      (codePoint >= 1 && codePoint <= 8) ||
      codePoint === 11 ||
      codePoint === 12 ||
      (codePoint >= 14 && codePoint <= 31)
    ) {
      return invalidSvg('SVG contains unsupported control characters.');
    }
  }

  const stack: string[] = [];
  let rootSeen = false;
  let rootClosed = false;
  let xmlDeclarationSeen = false;
  let styleDepth = 0;
  let cursor = 0;

  while (cursor < markup.length) {
    if (markup[cursor] !== '<') {
      const nextTag = markup.indexOf('<', cursor);
      const text = markup.slice(
        cursor,
        nextTag === -1 ? markup.length : nextTag
      );
      if (rootClosed && text.trim()) {
        return invalidSvg('SVG contains text outside its root element.');
      }
      if (!rootSeen && text.trim()) {
        return invalidSvg('SVG contains text before its root element.');
      }
      const entityError = assertXmlEntityReferences(text);
      if (entityError) return entityError;
      if (styleDepth > 0) {
        const cssError = assertSafeCss(text);
        if (cssError) return cssError;
      }
      cursor = nextTag === -1 ? markup.length : nextTag;
      continue;
    }

    if (markup.startsWith('<!--', cursor)) {
      const commentEnd = markup.indexOf('-->', cursor + 4);
      if (commentEnd === -1)
        return invalidSvg('SVG contains an unterminated comment.');
      cursor = commentEnd + 3;
      continue;
    }

    if (markup.startsWith('<![CDATA[', cursor)) {
      const cdataEnd = markup.indexOf(']]>', cursor + 9);
      if (cdataEnd === -1)
        return invalidSvg('SVG contains unterminated CDATA.');
      const cdata = markup.slice(cursor + 9, cdataEnd);
      if (rootClosed || !rootSeen) {
        return invalidSvg('SVG CDATA must be inside its root element.');
      }
      if (styleDepth > 0) {
        const cssError = assertSafeCss(cdata);
        if (cssError) return cssError;
      }
      cursor = cdataEnd + 3;
      continue;
    }

    if (markup.startsWith('<?', cursor)) {
      const instructionEnd = markup.indexOf('?>', cursor + 2);
      if (instructionEnd === -1) {
        return invalidSvg(
          'SVG contains an unterminated processing instruction.'
        );
      }
      const instruction = markup.slice(cursor + 2, instructionEnd).trim();
      if (
        !/^xml(?:\s|$)/i.test(instruction) ||
        xmlDeclarationSeen ||
        rootSeen
      ) {
        return invalidSvg('SVG processing instructions are not allowed.');
      }
      const encodingMatch = /\bencoding\s*=\s*(["'])(.*?)\1/i.exec(instruction);
      if (encodingMatch && encodingMatch[2].toLowerCase() !== 'utf-8') {
        return invalidSvg('SVG XML declarations must use UTF-8 encoding.');
      }
      xmlDeclarationSeen = true;
      cursor = instructionEnd + 2;
      continue;
    }

    if (markup.startsWith('<!', cursor)) {
      return invalidSvg(
        'SVG declarations, including DOCTYPE, are not allowed.'
      );
    }

    const tagEnd = findTagEnd(markup, cursor);
    if (tagEnd === -1) return invalidSvg('SVG contains an unterminated tag.');
    const parsed = parseSvgTag(markup.slice(cursor, tagEnd + 1));
    if ('code' in parsed) return parsed;

    const lowerName = parsed.name.toLowerCase();
    if (unsafeSvgElements.has(lowerName)) {
      return invalidSvg(`SVG element ${parsed.name} is not allowed.`);
    }

    for (const [name, attribute] of parsed.attributes) {
      if (name === 'xmlns' && attribute.value !== SVG_NAMESPACE) {
        return invalidSvg('SVG may declare only the SVG namespace.');
      }
      if (
        name.startsWith('xmlns:') &&
        !(name === 'xmlns:xlink' && attribute.value === XLINK_NAMESPACE)
      ) {
        return invalidSvg('SVG contains an unsupported XML namespace.');
      }
      const attributeError = assertSafeAttribute(name, attribute.value);
      if (attributeError) return attributeError;
    }

    if (!parsed.closing) {
      if (!rootSeen) {
        if (parsed.name !== 'svg') {
          return invalidSvg('SVG root element must be <svg>.');
        }
        if (parsed.attributes.get('xmlns')?.value !== SVG_NAMESPACE) {
          return invalidSvg('SVG root element must declare the SVG namespace.');
        }
        rootSeen = true;
      } else if (rootClosed || stack.length === 0) {
        return invalidSvg('SVG contains content outside its root element.');
      }

      if (lowerName === 'style' && !parsed.selfClosing) styleDepth += 1;
      if (!parsed.selfClosing) {
        stack.push(parsed.name);
      } else if (stack.length === 0) {
        rootClosed = true;
      }
    } else {
      if (!rootSeen || rootClosed || stack.length === 0) {
        return invalidSvg('SVG closing tag does not match an open element.');
      }
      const expectedName = stack[stack.length - 1];
      if (expectedName !== parsed.name) {
        return invalidSvg(
          `SVG closing tag for ${parsed.name} is out of order.`
        );
      }
      stack.pop();
      if (lowerName === 'style') styleDepth -= 1;
      if (stack.length === 0) rootClosed = true;
    }
    cursor = tagEnd + 1;
  }

  if (!rootSeen || !rootClosed || stack.length > 0) {
    return invalidSvg('SVG markup is not well formed.');
  }
  return null;
}

function normalizeSvgText(svg: string) {
  return svg.replace(/\r\n?/g, '\n').trim();
}

export function validateAndNormalizeAdminV2WorkspaceToolCatalogSvg(params: {
  bytes: SvgBytes;
  mimeType: string;
}): AdminV2WorkspaceToolCatalogSvgValidationResult {
  if (
    params.mimeType !== ADMIN_V2_WORKSPACE_TOOL_CATALOG_CUSTOM_ICON_MIME_TYPE
  ) {
    return {
      ok: false,
      code: 'invalid_mime_type',
      message: 'Custom Workspace Tool Catalog icons must be SVG files.',
    };
  }

  const bytes = toBytes(params.bytes);
  if (bytes.byteLength === 0) {
    return {
      ok: false,
      code: 'empty_svg',
      message: 'Custom Workspace Tool Catalog SVG cannot be empty.',
    };
  }
  if (
    bytes.byteLength > ADMIN_V2_WORKSPACE_TOOL_CATALOG_CUSTOM_ICON_MAX_BYTES
  ) {
    return {
      ok: false,
      code: 'svg_too_large',
      message: `Custom Workspace Tool Catalog SVG cannot exceed ${ADMIN_V2_WORKSPACE_TOOL_CATALOG_CUSTOM_ICON_MAX_BYTES} bytes.`,
    };
  }

  let decoded: string;
  try {
    decoded = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return {
      ok: false,
      code: 'invalid_utf8',
      message: 'Custom Workspace Tool Catalog SVG must be valid UTF-8.',
    };
  }

  const normalized = normalizeSvgText(decoded.replace(/^\uFEFF/, ''));
  if (!normalized) {
    return {
      ok: false,
      code: 'empty_svg',
      message: 'Custom Workspace Tool Catalog SVG cannot be empty.',
    };
  }
  const markupError = inspectSvgMarkup(normalized);
  if (markupError) {
    return { ok: false, ...markupError };
  }

  return {
    ok: true,
    svg: normalized,
    byteSize: bytes.byteLength,
  };
}
